import 'react';
import {Box, Stack, Typography} from '@mui/material';
import {InputField} from '@/pages/components/common';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import ProgramDateField from '@/pages/components/audit-program/ProgramDateField';

export interface ProgramSectionHeaderProps {
    header: AuditProgramSectionVO;
    refCode?: string;
    variant?: 'a1' | 'a12' | 'a13';
    values?: Record<string, string>;
    onChange?: (key: string, value: string) => void;
}

function HeaderField({
    label,
    fieldKey,
    value,
    onChange,
    minWidth = 50,
    readOnly = false,
}: {
    label: string;
    fieldKey: string;
    value?: string;
    onChange?: (key: string, value: string) => void;
    minWidth?: number;
    readOnly?: boolean;
}) {
    return (
        <Stack direction="row" spacing={1} alignItems="center" sx={{minWidth: 0, width: '100%'}}>
            <Typography variant="body2" color="text.secondary" sx={{whiteSpace: 'nowrap', minWidth, flexShrink: 0}}>
                {label}
            </Typography>
            <InputField
                type="text"
                fullWidth
                compact
                align="left"
                value={value ?? ''}
                InputProps={readOnly ? {readOnly: true} : undefined}
                onChange={readOnly ? undefined : (e) => onChange?.(fieldKey, e.target.value)}
            />
        </Stack>
    );
}

function HeaderDateField({
    label,
    fieldKey,
    value,
    onChange,
    minWidth = 40,
    readOnly = false,
}: {
    label: string;
    fieldKey: string;
    value?: string;
    onChange?: (key: string, value: string) => void;
    minWidth?: number;
    readOnly?: boolean;
}) {
    return (
        <Stack direction="row" spacing={1} alignItems="center" sx={{minWidth: 0, width: '100%'}}>
            <Typography variant="body2" color="text.secondary" sx={{whiteSpace: 'nowrap', minWidth, flexShrink: 0}}>
                {label}
            </Typography>
            {readOnly || !onChange ? (
                <InputField type="text" fullWidth compact align="left" value={value ?? ''} InputProps={{readOnly: true}}/>
            ) : (
                <ProgramDateField
                    value={value ?? ''}
                    onChange={(v) => onChange(fieldKey, v)}
                    caption={false}
                />
            )}
        </Stack>
    );
}

const headerGridSx = {
    display: 'grid',
    gridTemplateColumns: {
        xs: '1fr',
        md: 'minmax(0, 2.2fr) minmax(0, 1.3fr) minmax(0, 1.1fr) minmax(0, 0.8fr)',
    },
    columnGap: 1.5,
    rowGap: 0.75,
    mb: 1,
    alignItems: 'center',
} as const;

/**
 * Excel-style header rows:
 * A1.2: Client | Completed by | Date | Ref
 *       Year end | Reviewed by | Date | A1.2
 *       File no
 * A1.3: same with Prepared by
 */
export default function ProgramSectionHeader({
    header,
    refCode,
    variant = 'a1',
    values = {},
    onChange,
}: ProgramSectionHeaderProps) {
    const editable = Boolean(onChange);

    if (variant === 'a12' || variant === 'a13') {
        const staffLabel = variant === 'a12' ? 'Completed by:' : 'Prepared by:';
        const staffKey = variant === 'a12' ? 'completedBy' : 'preparedBy';
        const staffFallback = header.preparedInitial || header.preparedBy || '';
        const reviewedFallback = header.reviewedInitial || header.reviewedBy || '';
        const dateKey1 = variant === 'a12' ? 'completedDate' : 'preparedDate';
        const defaultRef = refCode ?? (variant === 'a12' ? 'A1.2' : 'A1.3');

        return (
            <Box sx={headerGridSx}>
                {/* Row 1 */}
                <HeaderField
                    label="Client:"
                    fieldKey="client"
                    value={values.client ?? header.client ?? ''}
                    onChange={onChange}
                    minWidth={72}
                    readOnly={!editable}
                />
                <HeaderField
                    label={staffLabel}
                    fieldKey={staffKey}
                    value={values[staffKey] ?? staffFallback}
                    onChange={onChange}
                    minWidth={100}
                    readOnly={!editable}
                />
                <HeaderDateField
                    label="Date:"
                    fieldKey={dateKey1}
                    value={values[dateKey1]}
                    onChange={onChange}
                    minWidth={40}
                    readOnly={!editable}
                />
                <HeaderField
                    label="Ref:"
                    fieldKey="ref"
                    value={values.ref ?? defaultRef}
                    onChange={onChange}
                    minWidth={36}
                    readOnly={!editable}
                />

                {/* Row 2 — Year end / Reviewed by / Date share one grid row */}
                <HeaderField
                    label="Year end:"
                    fieldKey="yearEnd"
                    value={values.yearEnd ?? header.yearEnd ?? ''}
                    onChange={onChange}
                    minWidth={72}
                    readOnly={!editable}
                />
                <HeaderField
                    label="Reviewed by:"
                    fieldKey="reviewedBy"
                    value={values.reviewedBy ?? reviewedFallback}
                    onChange={onChange}
                    minWidth={100}
                    readOnly={!editable}
                />
                <HeaderDateField
                    label="Date:"
                    fieldKey="reviewedDate"
                    value={values.reviewedDate}
                    onChange={onChange}
                    minWidth={40}
                    readOnly={!editable}
                />
                <Box sx={{display: {xs: 'none', md: 'block'}}}/>

                {/* Row 3 */}
                <HeaderField
                    label="File no:"
                    fieldKey="fileNo"
                    value={values.fileNo ?? header.fileNo ?? ''}
                    onChange={onChange}
                    minWidth={72}
                    readOnly={!editable}
                />
            </Box>
        );
    }

    const defaultRef = refCode ?? 'A1';

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: {xs: '1fr', md: 'minmax(0, 2fr) minmax(0, 1fr)'},
                columnGap: 2,
                rowGap: 0.75,
                mb: 1,
                alignItems: 'center',
            }}
        >
            <HeaderField
                label="Client:"
                fieldKey="client"
                value={values.client ?? header.client ?? ''}
                onChange={onChange}
                minWidth={72}
                readOnly={!editable}
            />
            <HeaderField
                label="Ref:"
                fieldKey="ref"
                value={values.ref ?? defaultRef}
                onChange={onChange}
                minWidth={40}
                readOnly={!editable}
            />
            <HeaderField
                label="Year end:"
                fieldKey="yearEnd"
                value={values.yearEnd ?? header.yearEnd ?? ''}
                onChange={onChange}
                minWidth={72}
                readOnly={!editable}
            />
            <Box sx={{display: {xs: 'none', md: 'block'}}}/>
            <HeaderField
                label="File no:"
                fieldKey="fileNo"
                value={values.fileNo ?? header.fileNo ?? ''}
                onChange={onChange}
                minWidth={72}
                readOnly={!editable}
            />
        </Box>
    );
}
